import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles, type Article } from '@/mocks/articleData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<Article | null>(null);

  useEffect(() => {
    const found = articles.find((a) => a.slug === slug);
    setArticle(found || null);
    if (found) {
      document.title = `${found.title} - 禁漫天堂`;
    }
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
          <p className="mt-3 text-foreground-500">文章未找到</p>
          <Link to="/" className="mt-4 inline-block text-primary-500 text-sm hover:underline">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && (a.category === article.category || a.categorySlug === article.categorySlug))
    .slice(0, 3);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    dateModified: article.date,
    url: `https://jmtthub.com/article/${article.slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '禁漫天堂',
      url: 'https://jmtthub.com/',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen pt-16">
        <div className="w-full px-4 md:px-6 pt-6 pb-4">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '使用教程', path: '/article/register-guide' },
                { label: article.category, path: `/article/${articles.find(a => a.categorySlug === article.categorySlug)?.slug || articles[0].slug}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <article className="flex-1 min-w-0">
                <div className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden">
                  <div className="aspect-[21/9] overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>

                  <div className="p-6 md:p-10">
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                      <span className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                        {article.category}
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                      <span className="text-xs text-foreground-400 flex items-center gap-1">
                        <i className="ri-user-line"></i> {article.author}
                      </span>
                    </div>

                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                      {article.title}
                    </h1>

                    <div
                      className="prose prose-sm max-w-none
                        prose-headings:text-foreground-900 prose-headings:font-heading prose-headings:font-bold
                        prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4
                        prose-p:text-foreground-600 prose-p:leading-relaxed prose-p:mb-4
                        prose-strong:text-foreground-800
                        prose-ul:list-disc prose-ul:pl-5 prose-ul:text-foreground-600
                        prose-ol:list-decimal prose-ol:pl-5 prose-ol:text-foreground-600
                        prose-li:mb-2 prose-li:leading-relaxed
                        [&_ul]:space-y-1 [&_ol]:space-y-1
                      "
                      dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    <div className="mt-8 pt-6 border-t border-background-200/70">
                      <div className="flex flex-wrap items-center gap-2">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="text-xs px-3 py-1.5 rounded-full bg-background-100 text-foreground-500"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="lg:w-[300px] flex-shrink-0">
                <div className="lg:sticky lg:top-24 space-y-5">
                  {/* Related Articles */}
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <h3 className="text-base font-heading font-bold text-foreground-800 mb-4 flex items-center gap-2">
                      <i className="ri-article-line text-primary-500"></i>
                      相关文章
                    </h3>
                    <div className="space-y-4">
                      {relatedArticles.map((ra) => (
                        <Link
                          key={ra.slug}
                          to={`/article/${ra.slug}`}
                          className="block group"
                        >
                          <h4 className="text-sm font-medium text-foreground-700 group-hover:text-primary-500 transition-colors line-clamp-2">
                            {ra.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5">
                            <span className="text-xs text-foreground-400">{ra.date}</span>
                            <span className="text-xs text-foreground-300">·</span>
                            <span className="text-xs text-foreground-400">{ra.readTime}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Quick Links */}
                  <div className="bg-background-50 rounded-xl border border-background-200/70 p-5">
                    <h3 className="text-base font-heading font-bold text-foreground-800 mb-4 flex items-center gap-2">
                      <i className="ri-links-line text-primary-500"></i>
                      快捷入口
                    </h3>
                    <div className="space-y-2">
                      <a
                        href="#"
                        className="flex items-center gap-2.5 p-3 rounded-lg bg-primary-50 hover:bg-primary-100 transition-colors cursor-pointer"
                      >
                        <i className="ri-link text-primary-500"></i>
                        <span className="text-sm text-primary-600 font-medium">最新入口</span>
                      </a>
                      <Link
                        to="/category/riman"
                        className="flex items-center gap-2.5 p-3 rounded-lg hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-stack-line text-foreground-500"></i>
                        <span className="text-sm text-foreground-600">分类导航</span>
                      </Link>
                      <Link
                        to="/about"
                        className="flex items-center gap-2.5 p-3 rounded-lg hover:bg-background-100 transition-colors"
                      >
                        <i className="ri-information-line text-foreground-500"></i>
                        <span className="text-sm text-foreground-600">关于我们</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>

        {/* More Articles */}
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              更多使用指南
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 6)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 transition-all duration-300 p-5 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700">
                        {a.category}
                      </span>
                      <span className="text-xs text-foreground-400">{a.date}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-800 mb-2 group-hover:text-primary-500 transition-colors line-clamp-2">
                      {a.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{a.excerpt}</p>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}